<script setup lang="ts">
import { computed, type PropType, provide, ref } from "vue";
import type { UIColor } from "../../..";
import { SimpleTableContextInjectionKey } from "../../helpers.js";

const props = defineProps({
  /*
      Set height to 100%
    */
  explodeHeight: {
    type: Boolean,
    default: false,
  },

  /*
      Enable scroll-snap
      NOTE: does not work well with nested tables
      See https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Scroll_Snap
    */
  scrollSnap: {
    type: Boolean,
    default: false,
  },

  /*
      Omit padding on table
    */
  omitPadding: {
    type: Boolean,
    default: false,
  },

  colorScrollbarForeground: {
    type: String as PropType<UIColor>,
    default: "--color-accent-300",
  },

  colorScrollbarBackground: {
    type: String as PropType<UIColor>,
    default: "--color-white",
  },

  fixedHeader: {
    type: Boolean,
    default: false,
  },
});

const containsRenderAsCard = ref(false);
const containsRenderAsCardExtension = ref(false);

const classList = computed(() => {
  return [
    "clip-path",
    "simple-table",
    props.scrollSnap ? "scrollSnap" : "",
    props.omitPadding ? "omitPadding" : "",
    containsRenderAsCard.value ? "containsRenderAsCard" : "",
    containsRenderAsCardExtension.value ? "containsRenderAsCardExtension" : "",
  ];
});

provide(SimpleTableContextInjectionKey, {
  containsRenderAsCard,
  containsRenderAsCardExtension,
});
</script>

<template>
  <div
    dir="ltr"
    :class="[
      $style.container,
      explodeHeight && $style.explodeHeight,
      ...classList,
    ]"
    :style="{
      '--simple-table-color-scrollbar-foreground': `var(${colorScrollbarForeground})`,
      '--simple-table-color-scrollbar-background': `var(${colorScrollbarBackground})`,
    }"
  >
    <table :class="$style.table">
      <thead
        :class="fixedHeader && $style.fixedHeader"
        v-if="$slots.thead?.length"
      >
        <slot name="thead"></slot>
      </thead>
      <tbody :class="$style.tbody">
        <slot />
      </tbody>
    </table>
  </div>
</template>

<style module>
@import url("./css/shadows.css");
@import url("./css/hover-effect.css");

.containerOuter {
  display: flex;
  width: 100%;
  position: relative;
}

.container {
  display: block;
  width: 100%;
  height: 100%;
  overflow: auto;
  clip-path: inset(0 0 0 0 round var(--border-radius-medium));
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: var(--simple-table-color-scrollbar-foreground)
    var(--simple-table-color-scrollbar-background);
  scrollbar-width: thin;
}

.fixedHeader {
  position: sticky;
  top: 0;
  z-index: 3;
}

/* .overflow {
  width: 2.5rem;
  height: 100%;
  background-color: var(--color-supportive-3-100);
  background-image: url("./assets/test.svg");
  background-repeat: repeat;
  background-position: top center;
} */

.container::-webkit-scrollbar {
  height: 1.5rem;
  width: 1.5rem;
}

.container::-webkit-scrollbar-track {
  background: var(--simple-table-color-scrollbar-background);
}

.container::-webkit-scrollbar-thumb {
  background-color: var(--simple-table-color-scrollbar-foreground);
  border-radius: calc(1.5rem - var(--border-radius-medium));
  border: 0.5rem solid var(--simple-table-color-scrollbar-background);
}

.container::-webkit-scrollbar-thumb:hover {
  border-width: 0.45rem;
}

.container::-webkit-scrollbar-corner {
  background-color: var(--simple-table-color-scrollbar-background);
}

.explodeHeight {
  height: 100%;
}

.table {
  width: 100%;
  border: none;
  border-collapse: collapse;
  border-spacing: 0;
}

:global(.simple-table.containsRenderAsCard table) {
  border-collapse: separate;
  border-spacing: 0 1rem;
}

:global(
    .simple-table.containsRenderAsCard
      table
      tr:first-child:not(.renderAsCard)
      td
  ) {
  padding-top: unset;
  padding-bottom: unset;
}

:global(.simple-table.containsRenderAsCardExtension table) {
  border-spacing: 0 0.5rem;
}

:global(
    .simple-table.containsRenderAsCardExtension
      table
      tr:first-child:not(.renderAsCard)
      td
  ) {
  padding-top: unset;
  padding-bottom: 0.5rem;
}

:global(.simple-table.scrollSnap) {
  scroll-snap-type: y proximity;
}

:global(.simple-table.scrollSnap tr) {
  scroll-snap-align: top;
}

:global(.simple-table.scrollSnap td) {
  scroll-snap-align: start;
}

:global(.simple-table.omitPadding tr:only-child td) {
  padding-top: unset;
  padding-bottom: unset;
}

:global(.simple-table.omitPadding td:first-child) {
  padding-left: unset;
}

:global(.simple-table.omitPadding td:last-child) {
  padding-right: unset;
}
</style>
